import http from "./httpService";
import { getConfig } from "../config/config.js";

const config = getConfig();
const serviceEndpoint = config.apiEndpoint + "/torneos";

export async function getTorneo(recordId) {
  try {
    const response = await http.get(serviceEndpoint + "/" + recordId);
    return response.data.record;
  } catch (error) {
    if (error.message.toUpperCase().includes("NETWORK ERROR")) return error.message.toUpperCase();
    else return [];
  }
}

export async function getTorneos() {
  try {
    const response = await http.get(serviceEndpoint);
    return response.data.records;
  } catch (error) {
    if (error.message.toUpperCase().includes("NETWORK ERROR")) return error.message.toUpperCase();
    else return [];
  }
}

export async function saveTorneo(data, parentId) {
  if (data._id) return await http.put(serviceEndpoint + "/" + data._id, data);
  else {
    if (parentId) data.parentId = parentId;
    const response = await http.post(serviceEndpoint, data);
    return response;
  }
}

// ---- Guardados granulares -------------------------------------------------
//
// `saveTorneo` manda el torneo entero, así que dos dispositivos guardando a la vez
// se pisan: gana el último, con la copia que tenía en memoria. Durante una fecha eso
// pasa siempre — varias personas cargando en paralelo desde el celular la lista de
// fe, los partidos y los resultados.
//
// Por eso todo eso se guarda con estos endpoints, que escriben un solo subdocumento
// (y sólo los campos que se les mandan). `saveTorneo` quedó para la estructura:
// nombre, año, qué equipos juegan, ruedas y zonas.
//
// Cada llamada nombra lo que toca con los _id de Mongo de rueda/zona/fecha/partido
// (los genera el cliente al crear, ver components/torneos/ids.js) y contesta con el
// torneo entero ya actualizado.

const torneoUrl = (torneoId, recurso) => serviceEndpoint + "/" + torneoId + recurso;

// Alta de una fecha en una zona. Idempotente: si la fecha ya está no se duplica.
export async function crearFecha(torneoId, datos) {
  const response = await http.post(torneoUrl(torneoId, "/fecha"), datos);
  return response.data;
}

// Día y sede de una fecha.
export async function guardarFecha(torneoId, datos) {
  const response = await http.patch(torneoUrl(torneoId, "/fecha"), datos);
  return response.data;
}

// Baja de una fecha, con sus partidos y sus resultados.
export async function eliminarFecha(torneoId, datos) {
  const response = await http.delete(torneoUrl(torneoId, "/fecha"), { data: datos });
  return response.data;
}

// La lista de fe de UN equipo en una fecha. Va equipo por equipo: así dos personas
// cargando la lista de fe de dos equipos de la misma fecha no se pisan.
export async function guardarListaFeDeFecha(torneoId, datos) {
  const response = await http.put(torneoUrl(torneoId, "/fecha/lista-fe"), datos);
  return response.data;
}

// Reemplaza todos los partidos de una fecha: es la importación del fixture, que
// trae la fecha entera de una.
export async function guardarPartidosDeFecha(torneoId, datos) {
  const response = await http.put(torneoUrl(torneoId, "/fecha/partidos"), datos);
  return response.data;
}

// Alta de un partido en una fecha. Idempotente, igual que el alta de fecha.
export async function crearPartido(torneoId, datos) {
  const response = await http.post(torneoUrl(torneoId, "/partido"), datos);
  return response.data;
}

// Escribe un solo partido: el resultado (jugado / wo / goleadores) desde la pantalla
// de resultados, la programación (equipos, hora y cancha) desde el ABM del torneo.
// Se escriben únicamente los campos que se mandan, así que una no pisa a la otra.
export async function guardarPartido(torneoId, datos) {
  const response = await http.patch(torneoUrl(torneoId, "/partido"), datos);
  return response.data;
}

// Baja de un partido, con el resultado que tuviera cargado.
export async function eliminarPartido(torneoId, datos) {
  const response = await http.delete(torneoUrl(torneoId, "/partido"), { data: datos });
  return response.data;
}

// La lista de fe de un equipo EN EL TORNEO (el roster del que después salen las
// listas de fe de cada fecha).
export async function guardarListaFeDeEquipo(torneoId, datos) {
  const response = await http.put(torneoUrl(torneoId, "/equipo/lista-fe"), datos);
  return response.data;
}

// Saca un equipo de una zona. La poda de lo que colgaba de él —lista de fe y
// partidos de las fechas de esa zona— la hace el servidor sobre lo que hay en la
// base, no el cliente sobre su copia, justamente para no pisar a los demás.
export async function sacarEquipoDeZona(torneoId, datos) {
  const response = await http.delete(torneoUrl(torneoId, "/zona/equipo"), { data: datos });
  return response.data;
}

// Saca un equipo del torneo: lo mismo, pero en todas las zonas de todas las ruedas.
export async function sacarEquipoDelTorneo(torneoId, datos) {
  const response = await http.delete(torneoUrl(torneoId, "/equipo"), { data: datos });
  return response.data;
}

// Sube el Excel del fixture y devuelve las filas crudas que el server pudo leer.
// El endpoint NO escribe nada: sólo parsea, para poder previsualizar antes de
// tocar el torneo. La resolución de nombres a equipos la hace el cliente
// (components/torneos/importFixture.js), que tiene el torneo en memoria.
export async function importarFixture(file) {
  const formData = new FormData();
  formData.append("files", file); // mismo nombre de campo que espera req.files.files
  try {
    const response = await http.post(serviceEndpoint + "/importar-fixture", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      (error.message.toUpperCase().includes("NETWORK ERROR")
        ? "No se pudo conectar con el servidor"
        : "No se pudo leer el archivo");
    return { success: false, message };
  }
}

// Sube el cuadro de cruces del año y devuelve, por cada solapa "Zona X", los nombres
// de equipo que aparecen adentro. Igual que el fixture, el endpoint NO escribe nada:
// resolver esos nombres contra los equipos del sistema y armar la rueda lo hace el
// cliente (components/torneos/importEstructura.js).
export async function importarEstructura(file) {
  const formData = new FormData();
  formData.append("files", file); // mismo nombre de campo que espera req.files.files
  try {
    const response = await http.post(serviceEndpoint + "/importar-estructura", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      (error.message.toUpperCase().includes("NETWORK ERROR")
        ? "No se pudo conectar con el servidor"
        : "No se pudo leer el archivo");
    return { success: false, message };
  }
}

export async function deleteTorneo(recordId) {
  return await http.delete(serviceEndpoint + "/" + recordId);
}

export async function getTorneosSelect() {
  let items = [];
  try {
    const response = await http.get(serviceEndpoint);
    response.data.records.map((item) => {
      items.push({ value: item._id, label: item.nombre });
    });
    return items;
  } catch (error) {
    if (error.message.toUpperCase().includes("NETWORK ERROR")) return error.message.toUpperCase();
    else return [];
  }
}
